import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import axios from 'axios';
import { api2faSetup } from '../api/auth';
import { useAuth } from '../context/AuthContext';

function extractError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    return (err.response?.data as { error?: { message?: string } } | undefined)
      ?.error?.message ?? err.message;
  }
  return err instanceof Error ? err.message : 'An error occurred';
}

// ── Shared layout wrapper ────────────────────────────────────────────────────

function LoginCard({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <Paper sx={{ p: 4, maxWidth: 400, width: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <LockOutlinedIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          <Typography variant="h5" component="h1">WebAdmin</Typography>
        </Box>
        {children}
      </Paper>
    </Box>
  );
}

// ── Step 1: Credentials ──────────────────────────────────────────────────────

function CredentialsStep() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password);
    } catch (err) {
      setError(extractError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginCard>
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
        Sign in to continue
      </Typography>
      <Box component="form" onSubmit={(e) => void handleSubmit(e)} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Username"
          autoComplete="username"
          autoFocus
          fullWidth
          value={username}
          onChange={e => setUsername(e.target.value)}
          disabled={loading}
          error={!!error}
        />
        <TextField
          label="Password"
          type="password"
          autoComplete="current-password"
          fullWidth
          value={password}
          onChange={e => setPassword(e.target.value)}
          disabled={loading}
          error={!!error}
          helperText={error || undefined}
        />
        <Button
          type="submit"
          variant="contained"
          fullWidth
          size="large"
          disabled={loading || !username || !password}
          sx={{ mt: 1 }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
        </Button>
      </Box>
    </LoginCard>
  );
}

// ── Step 2a: TOTP verify ─────────────────────────────────────────────────────

function TotpVerifyStep() {
  const { verify2fa, logout } = useAuth();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verify2fa(code, useRecovery ? 'recovery' : 'totp');
    } catch (err) {
      setError(extractError(err));
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginCard>
      <Typography variant="subtitle1" align="center" gutterBottom>
        Two-Factor Authentication
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
        {useRecovery
          ? 'Enter one of your recovery codes.'
          : 'Enter the 6-digit code from your authenticator app.'}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box component="form" onSubmit={(e) => void handleSubmit(e)} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label={useRecovery ? 'Recovery code' : 'Authenticator code'}
          autoComplete={useRecovery ? 'off' : 'one-time-code'}
          autoFocus
          fullWidth
          value={code}
          onChange={e => setCode(e.target.value)}
          disabled={loading}
          placeholder={useRecovery ? 'xxxxxxxx-xxxxxxxx' : '000000'}
          inputProps={useRecovery ? {} : { maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
        />
        <Button type="submit" variant="contained" fullWidth size="large" disabled={loading || !code}>
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify'}
        </Button>
      </Box>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
        <Link component="button" variant="body2" onClick={() => { setCode(''); setError(''); setUseRecovery(v => !v); }}>
          {useRecovery ? 'Use authenticator code' : 'Use a recovery code'}
        </Link>
        <Link component="button" variant="body2" onClick={() => void logout()}>
          Back
        </Link>
      </Box>
    </LoginCard>
  );
}

// ── Step 2b: First-time 2FA setup ────────────────────────────────────────────

type SetupPhase = 'loading' | 'show_qr' | 'confirm';

function TwoFaSetupStep() {
  const { auth, verify2fa, logout } = useAuth();
  const [phase, setPhase] = useState<SetupPhase>('loading');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [confirmCode, setConfirmCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (auth.status !== 'setup_required' || fetchedRef.current) return;
    fetchedRef.current = true;
    api2faSetup(auth.pendingToken)
      .then(data => {
        setQrDataUrl(data.qrDataUrl);
        setRecoveryCodes(data.recoveryCodes);
        setPhase('show_qr');
      })
      .catch(err => setError(extractError(err)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.status]);

  const handleCopy = () => {
    void navigator.clipboard.writeText(recoveryCodes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verify2fa(confirmCode, 'totp');
    } catch (err) {
      setError(extractError(err));
      setConfirmCode('');
    } finally {
      setLoading(false);
    }
  };

  if (phase === 'loading') {
    return (
      <LoginCard>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          {error
            ? <Alert severity="error">{error}</Alert>
            : <CircularProgress />}
        </Box>
      </LoginCard>
    );
  }

  if (phase === 'show_qr') {
    return (
      <LoginCard>
        <Typography variant="subtitle1" align="center" gutterBottom>
          Set Up Two-Factor Authentication
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
          Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.).
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <img src={qrDataUrl} alt="TOTP QR code" width={180} height={180} />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="body2" fontWeight={600} gutterBottom>
          Save these recovery codes
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Store them somewhere safe. Each code can only be used once.
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, mb: 1 }}>
          {recoveryCodes.map(code => (
            <Typography key={code} variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
              {code}
            </Typography>
          ))}
        </Box>

        <Tooltip title={copied ? 'Copied!' : 'Copy all codes'}>
          <Button
            size="small"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopy}
            sx={{ mb: 2 }}
          >
            {copied ? 'Copied' : 'Copy all'}
          </Button>
        </Tooltip>

        <Button variant="contained" fullWidth onClick={() => setPhase('confirm')}>
          I've saved my codes — Continue
        </Button>
        <Button variant="text" fullWidth sx={{ mt: 1 }} onClick={() => void logout()}>
          Cancel
        </Button>
      </LoginCard>
    );
  }

  // phase === 'confirm'
  return (
    <LoginCard>
      <Typography variant="subtitle1" align="center" gutterBottom>
        Confirm your authenticator
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
        Enter the 6-digit code shown in your app to complete setup.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box component="form" onSubmit={(e) => void handleConfirm(e)} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Authenticator code"
          autoComplete="one-time-code"
          autoFocus
          fullWidth
          value={confirmCode}
          onChange={e => setConfirmCode(e.target.value)}
          disabled={loading}
          placeholder="000000"
          inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
        />
        <Button type="submit" variant="contained" fullWidth size="large" disabled={loading || confirmCode.length !== 6}>
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Confirm Setup'}
        </Button>
      </Box>
      <Button variant="text" fullWidth sx={{ mt: 1 }} onClick={() => setPhase('show_qr')}>
        Back to QR code
      </Button>
    </LoginCard>
  );
}

// ── Root login component ─────────────────────────────────────────────────────

export function Login() {
  const { auth } = useAuth();

  if (auth.status === 'authenticated') return <Navigate to="/dashboard" replace />;
  if (auth.status === 'pending_2fa') return <TotpVerifyStep />;
  if (auth.status === 'setup_required') return <TwoFaSetupStep />;
  return <CredentialsStep />;
}
